import { pipe } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AppConfig, appConfigFeature } from '../reducers/app-config.reducer';
import { select } from '@ngrx/store';

const appConfigValueByKey = (
  appConfig: AppConfig,
  key: string,
  fallbackValue?: any,
) => (appConfig?.[key] !== undefined ? appConfig[key] : fallbackValue);

const appConfigValueByDataAndResource = (
  appConfig: AppConfig,
  data: string,
  resource: string,
) => {
  const dataConfig = appConfig?.data?.[data];

  if (typeof dataConfig === 'string') {
    return resource ? `${dataConfig}/${resource}` : dataConfig;
  } else if (typeof dataConfig?.resources?.[resource] === 'string') {
    return dataConfig.resources[resource];
  } else if (typeof dataConfig?.resources?.[resource]?.baseUrl === 'string') {
    return dataConfig.resources[resource].baseUrl;
  } else if (typeof dataConfig?.baseUrl === 'string') {
    return resource ? `${dataConfig.baseUrl}/${resource}` : dataConfig.baseUrl;
  }

  const resourceConfig = appConfig?.resources?.[resource];
  if (typeof resourceConfig === 'string') {
    return resourceConfig;
  } else if (typeof resourceConfig?.baseUrl === 'string') {
    return resourceConfig?.baseUrl;
  }
};

export const appConfigSelector = appConfigFeature.selectAppConfig;

export const selectAppConfig = pipe(
  select(appConfigSelector),
  filter((appConfig) => appConfig != null),
  take(1),
);

export const selectAppConfigValueByKey = (
  key: string,
  fallbackValue?: string,
) =>
  pipe(
    selectAppConfig,
    map((appConfig) => appConfigValueByKey(appConfig, key, fallbackValue)),
  );

export const selectAppConfigValueByDataAndResource = (
  data: string,
  resource: string,
) =>
  pipe(
    selectAppConfig,
    map((appConfig) =>
      appConfigValueByDataAndResource(appConfig, data, resource),
    ),
  );

export const selectDocumentManifestSource = selectAppConfigValueByKey(
  'documentManifest-source',
  'urn:oid:2.999.1.1.1.1.5',
);

export const selectStartPageUrl = selectAppConfigValueByKey('start-page');
export const selectDPO = selectAppConfigValueByKey('dpo');
export const selectInactiveUserUrl =
  selectAppConfigValueByKey('inactive-user-url');
export const selectPermissionManagerUrl = selectAppConfigValueByKey(
  'permission-manager-url',
);
export const selectPatientIdRoot = selectAppConfigValueByKey('patient-id-root');
export const selectLogoUrl = selectAppConfigValueByKey('logo-url');
export const selectEnvironment = selectAppConfigValueByKey('environmentHint');
export const selectPortalVersion = selectAppConfigValueByKey('portalVersion');

export const selectLocationDistance =
  selectAppConfigValueByKey('location-distance');
export const selectLocationDistanceUnit = selectAppConfigValueByKey(
  'location-distance-unit',
);

export const selectInformationPortalUrl = selectAppConfigValueByKey(
  'information-portal-url',
);
export const selectEmergencyUrl = selectAppConfigValueByKey('emergency-url');

export const selectOpenStreetMapUrl =
  selectAppConfigValueByKey('openStreetMap-url');

export const selectKeepAliveClientId =
  selectAppConfigValueByKey('keepAliveClientId');
export const selectKeepAliveClientSecret = selectAppConfigValueByKey(
  'keepAliveClientSecret',
);

export const selectIsLogoutBy401Error = pipe(
  selectAppConfigValueByKey('no-logout-by-401-error'),
  map((value) => value.toLowerCase() !== 'true'),
);

export const selectIsHideLanguageSelection = pipe(
  selectAppConfigValueByKey('isHideLanguageSelection'),
  map((value) => value === 'true'),
);

export const selectAddTranslationKeysLanguage = pipe(
  selectAppConfigValueByKey('addTranslationKeysLanguage'),
  map((value) => value === 'true'),
);

export const selectMinutesUntilAutoLogout = selectAppConfigValueByKey(
  'minutesUntilAutoLogout',
);

export const selectDaysNoticePeriodToCancelAccount = pipe(
  selectAppConfigValueByKey('daysNoticePeriodToCancelAccount'),
  map((days) => days || 14),
);

export const selectMessagingRootId =
  selectAppConfigValueByKey('messagingRootId');
